import { CheckoutData, PromotionRequirement, Service } from "@lemmonhq/types";
import {
  getCheckoutItems,
  getItemProductId,
  getItemQuantity,
  getProductIdsFromCategories,
} from "./item-utils";

export function validateAllRequirements(
  services: Service[],
  requirements: PromotionRequirement[],
  checkout: CheckoutData,
): boolean {
  console.log(
    "[PROMO-REQ] Validating",
    requirements.length,
    "requirements:",
    requirements,
  );

  const results = requirements.map((requirement, index) => {
    const result = validateSingleRequirement(services, requirement, checkout);
    console.log(
      `[PROMO-REQ] Requirement ${index + 1} (${requirement.type}):`,
      result ? "✅ PASS" : "❌ FAIL",
      requirement,
    );
    return result;
  });

  const allPassed = results.every((r) => r);
  console.log("[PROMO-REQ] Overall result:", allPassed ? "✅ PASS" : "❌ FAIL");
  return allPassed;
}

function validateSingleRequirement(
  services: Service[],
  requirement: PromotionRequirement,
  checkout: CheckoutData,
): boolean {
  switch (requirement.type) {
    case "service_method":
      return validateServiceMethodRequirement(requirement, checkout);

    case "context":
      return validateContextRequirement(requirement, checkout);

    case "product":
      return validateProductRequirement(requirement, checkout);

    case "product_category":
      return validateProductCategoryRequirement(
        services,
        requirement,
        checkout,
      );

    case "coin":
      return validateCoinRequirement(requirement, checkout);

    case "membership":
      return Boolean(
        checkout.customer?.membership_program_ids?.includes(
          requirement.membership_program_id,
        ),
      );

    default:
      console.warn(`Unknown requirement type: ${(requirement as any).type}`);
      return false;
  }
}

function validateServiceMethodRequirement(
  requirement: { value: string },
  checkout: CheckoutData,
): boolean {
  console.log(
    "[PROMO-REQ] Service method requirement - required:",
    requirement.value,
  );
  console.log(
    "[PROMO-REQ] Service method requirement - actual:",
    checkout.serviceMethod,
  );
  const passes = checkout.serviceMethod === requirement.value;
  console.log(
    `[PROMO-REQ] Service method result: ${passes ? "✅ PASS" : "❌ FAIL"}`,
  );
  return passes;
}

function validateContextRequirement(
  requirement: { key: string; value: boolean | string | number },
  checkout: CheckoutData,
): boolean {
  if (checkout.payload?.type === "cart") {
    const cart = checkout.payload.object;
    switch (requirement.key) {
      case "tableId":
        return cart.tableId === requirement.value;
      case "isMultiCourse":
        return cart.isMultiCourse === requirement.value;
      case "customerCount":
        return cart.customers.length === requirement.value;
      case "hasSchedule":
        return (cart.schedule !== undefined) === requirement.value;
      default:
        return false;
    }
  } else if (checkout.payload?.type === "table") {
    const tableCheckout = checkout.payload.object;
    switch (requirement.key) {
      case "tableId":
        return tableCheckout.table_id === requirement.value;
      default:
        return false;
    }
  }
  return false;
}

function validateProductRequirement(
  requirement: { product_ids: number[]; quantity: number },
  checkout: CheckoutData,
): boolean {
  const items = getCheckoutItems(checkout);
  console.log(
    "[PROMO-REQ] Product requirement - required product_ids:",
    requirement.product_ids,
  );
  console.log(
    "[PROMO-REQ] Product requirement - required quantity:",
    requirement.quantity,
  );
  console.log("[PROMO-REQ] Checkout items found:", items.length);

  // Count qualifying products across all items
  let totalQualifyingQuantity = 0;

  for (const item of items) {
    const productId = getItemProductId(item);
    const quantity = getItemQuantity(item);
    console.log(
      `[PROMO-REQ] Checking item - productId: ${productId}, quantity: ${quantity}`,
    );

    if (requirement.product_ids.includes(productId)) {
      console.log(
        `[PROMO-REQ]   ✅ Product ${productId} matches! Adding ${quantity} to total`,
      );
      totalQualifyingQuantity += quantity;
    } else {
      console.log(`[PROMO-REQ]   ⚠️ Product ${productId} not in required list`);
    }
  }

  console.log(
    `[PROMO-REQ] Total qualifying quantity: ${totalQualifyingQuantity}, required: ${requirement.quantity}`,
  );
  const passes = totalQualifyingQuantity >= requirement.quantity;
  console.log(
    `[PROMO-REQ] Product requirement result: ${passes ? "✅ PASS" : "❌ FAIL"}`,
  );

  return passes;
}

function validateProductCategoryRequirement(
  services: Service[],
  requirement: { category_ids: number[]; quantity: number },
  checkout: CheckoutData,
): boolean {
  const items = getCheckoutItems(checkout);

  // Get all product IDs from the required categories
  const qualifyingProductIds = getProductIdsFromCategories(
    services,
    requirement.category_ids,
  );

  // Count qualifying items
  let totalQualifyingQuantity = 0;

  for (const item of items) {
    const productId = getItemProductId(item);
    if (qualifyingProductIds.includes(productId)) {
      totalQualifyingQuantity += getItemQuantity(item);
    }
  }

  return totalQualifyingQuantity >= requirement.quantity;
}

function validateCoinRequirement(
  requirement: { coin_program_id?: string; coin_type: string; amount: number },
  checkout: CheckoutData,
): boolean {
  // Pure lookup: balances are resolved at the call site and passed in, so the
  // promotion engine stays synchronous. An absent customer (anonymous walk-in)
  // or an unknown program must fail closed — returning true on a missing
  // balance would hand out free items. See
  // docs/plans/loyalty/redemption_wiring.md.
  if (!requirement.coin_program_id) return false;
  const balance =
    checkout.customer?.coin_balances?.[requirement.coin_program_id];
  if (balance === undefined) return false;
  return balance >= requirement.amount;
}
